// ScheduleViewingModal — picks a property + buyer, sets the time.
// Layout adapts based on case type (property cases pre-pin the listing,
// client cases ask for a property; orphan opens show both fields).

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AIHint,
  Btn,
  Chip,
  Field,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalShell,
  TextArea,
  TextInput,
} from "./ModalShell";
import type { ScheduleViewingCtx } from "./FlowsProvider";
import { createViewing } from "@/lib/proagentApi";
import { invalidateCases } from "@/lib/agentCases";

// Source enum on this side maps to ViewingSource on the backend.
// "Direct" / "Repeat" don't have direct backend equivalents — we
// project them to "manual"; the rich source label is preserved in
// the `notes` field so it isn't lost.
const SOURCES = ["Direct", "PropertyGuru", "99.co", "Referral", "Repeat"];

function mapSource(s?: string): "manual" | "from_chat" | "from_buyer_app" {
  // Only one of the four options is a 1:1 map to the backend enum;
  // we keep the wider FE vocabulary in the notes blob.
  if (s === "Repeat") return "from_buyer_app";
  return "manual";
}

// Parses "30 min" / "30" / "1.5 hr" → minutes (default 30).
function parseDurationMin(s?: string): number {
  if (!s) return 30;
  const trimmed = s.trim().toLowerCase();
  const m = /^(\d+(?:\.\d+)?)\s*(min|m|hr|h)?/.exec(trimmed);
  if (!m) return 30;
  const num = parseFloat(m[1]);
  const unit = m[2] || "min";
  const isHour = unit === "hr" || unit === "h";
  const mins = Math.round(isHour ? num * 60 : num);
  return Math.max(5, Math.min(mins, 8 * 60)); // clamp 5min–8h
}

type Form = {
  what?: string;
  who?: string;
  date?: string;
  time?: string;
  duration?: string;
  source?: string;
  notes?: string;
};

export function ScheduleViewingModal({
  open,
  ctx,
  onClose,
}: {
  open: boolean;
  ctx: ScheduleViewingCtx | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<Form>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(ctx?.prefill ?? { duration: "30 min" });
      setError(null);
    }
  }, [open, ctx]);

  const isProperty = ctx?.caseObj?.type === "property";
  const isClient = ctx?.caseObj?.type === "client";
  const caseTitle = ctx?.caseObj?.shortName;
  // listing_id is required by the backend. For property cases we
  // pull it off the case (was wired through the adapter alongside
  // listing_id from /agent/cases). For client / orphan flows the
  // user fills the property text field, but we don't yet have a
  // resolver from text → listing_id, so those paths are surfaced
  // as a warning rather than failing silently.
  const listingId =
    ctx?.caseObj?.type === "property" ? ctx.caseObj.listingId : undefined;

  return (
    <ModalShell open={open} onClose={onClose} width={620} label="Schedule viewing">
      <ModalHeader
        eyebrow={t("agent.flows.scheduleViewing.eyebrow", { defaultValue: "Schedule viewing" })}
        title={t("agent.flows.scheduleViewing.title", { defaultValue: "New viewing" })}
        hint={
          caseTitle ? (
            <>
              {t("agent.flows.forCase", { defaultValue: "For case" })}{" "}
              <span style={{ color: "var(--ag-accent)", fontWeight: 600 }}>{caseTitle}</span>
            </>
          ) : (
            t("agent.flows.scheduleViewing.hint", {
              defaultValue: "Pick a property and a buyer, set the time.",
            })
          )
        }
        onClose={onClose}
      />

      <ModalBody>
        {isProperty ? (
          <Field label={t("agent.flows.scheduleViewing.buyer", { defaultValue: "Buyer" })}>
            <TextInput
              value={form.who}
              onChange={(v) => setForm((f) => ({ ...f, who: v }))}
              placeholder={t("agent.flows.scheduleViewing.buyerPh", {
                defaultValue: "Buyer name (or pick from leads)",
              })}
            />
          </Field>
        ) : isClient ? (
          <Field label={t("agent.flows.scheduleViewing.property", { defaultValue: "Property" })}>
            <TextInput
              value={form.what}
              onChange={(v) => setForm((f) => ({ ...f, what: v }))}
              placeholder={t("agent.flows.scheduleViewing.propertyPh", {
                defaultValue: "Pick from shortlist or paste address",
              })}
            />
          </Field>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label={t("agent.flows.scheduleViewing.property", { defaultValue: "Property" })}>
              <TextInput
                value={form.what}
                onChange={(v) => setForm((f) => ({ ...f, what: v }))}
                placeholder="Address / unit"
              />
            </Field>
            <Field label={t("agent.flows.scheduleViewing.buyer", { defaultValue: "Buyer" })}>
              <TextInput
                value={form.who}
                onChange={(v) => setForm((f) => ({ ...f, who: v }))}
                placeholder="Buyer name"
              />
            </Field>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr 1fr", gap: 12 }}>
          <Field label={t("agent.flows.scheduleViewing.date", { defaultValue: "Date" })}>
            {/* Native date/time inputs because the backend needs a
                strict RFC3339 timestamp. Free-text was nicer
                visually but the original implementation never
                actually parsed it. */}
            <TextInput
              type="date"
              mono
              value={form.date}
              onChange={(v) => setForm((f) => ({ ...f, date: v }))}
            />
          </Field>
          <Field label={t("agent.flows.scheduleViewing.time", { defaultValue: "Time" })}>
            <TextInput
              type="time"
              mono
              value={form.time}
              onChange={(v) => setForm((f) => ({ ...f, time: v }))}
            />
          </Field>
          <Field label={t("agent.flows.scheduleViewing.duration", { defaultValue: "Duration" })}>
            <TextInput
              mono
              value={form.duration}
              onChange={(v) => setForm((f) => ({ ...f, duration: v }))}
              placeholder="30 min"
            />
          </Field>
        </div>

        <Field label={t("agent.flows.scheduleViewing.source", { defaultValue: "Source" })}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {SOURCES.map((s) => (
              <Chip
                key={s}
                active={form.source === s}
                onClick={() => setForm((f) => ({ ...f, source: s }))}
              >
                {s}
              </Chip>
            ))}
          </div>
        </Field>

        <Field
          label={t("agent.flows.scheduleViewing.notes", { defaultValue: "Notes for the day" })}
          hint={t("agent.flows.scheduleViewing.notesHint", {
            defaultValue: "What to highlight, what to test, what to ask.",
          })}
        >
          <TextArea
            value={form.notes}
            onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
            rows={3}
            placeholder="e.g. Highlight high floor + freehold. Test budget flex toward $1.45M."
          />
        </Field>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 12px",
            background: "var(--ag-surface)",
            borderRadius: 8,
            border: "1px solid var(--ag-rule)",
            fontSize: 12.5,
          }}
        >
          <input type="checkbox" defaultChecked id="confirm-buyer" />
          <label htmlFor="confirm-buyer" style={{ color: "var(--ag-ink-2)" }}>
            {t("agent.flows.scheduleViewing.confirmSend", {
              defaultValue: "Send confirmation message to buyer with address & arrival notes",
            })}
          </label>
        </div>

        <AIHint>
          {t("agent.flows.scheduleViewing.ai", {
            defaultValue:
              "We'll add this to your calendar, set a 24h reminder, and prepare a 1-page brief (price history, comparable units, talking points) before the viewing.",
          })}
        </AIHint>
      </ModalBody>

      {error && (
        <div
          style={{
            margin: "0 16px 8px",
            padding: "8px 10px",
            background: "var(--ag-bad-bg, rgba(191,56,56,0.08))",
            border: "1px solid var(--ag-bad, #bf3838)",
            borderRadius: 6,
            color: "var(--ag-bad, #bf3838)",
            fontSize: 12.5,
          }}
        >
          {error}
        </div>
      )}

      <ModalFooter leftHint={t("agent.flows.escCancel", { defaultValue: "Esc to cancel" })}>
        <Btn ghost onClick={onClose} disabled={submitting}>
          {t("agent.flows.cancel", { defaultValue: "Cancel" })}
        </Btn>
        <Btn
          primary
          disabled={submitting}
          onClick={async () => {
            setError(null);
            // Backend requires date + time → RFC3339 ISO. Bail
            // early if either is missing rather than POSTing junk.
            if (!form.date || !form.time) {
              setError(t("agent.flows.scheduleViewing.missingDateTime", {
                defaultValue: "Date and time are both required.",
              }) as string);
              return;
            }
            const scheduledAt = new Date(`${form.date}T${form.time}`).toISOString();
            // Property case → linked listing. Client / orphan path
            // requires a listing_id we don't have a resolver for
            // yet; surface that as a clear-language error instead
            // of swallowing the click.
            if (!listingId) {
              setError(t("agent.flows.scheduleViewing.needPropertyCase", {
                defaultValue:
                  "Schedule from a property case for now — buyer-side viewings need a listing lookup that's coming soon.",
              }) as string);
              return;
            }
            setSubmitting(true);
            try {
              await createViewing({
                listing_id: listingId,
                buyer_name: form.who?.trim() ?? "",
                scheduled_at: scheduledAt,
                duration_min: parseDurationMin(form.duration),
                location: form.what?.trim() ?? "",
                source: mapSource(form.source),
                notes: [
                  form.source && form.source !== "Direct" ? `Source: ${form.source}` : "",
                  form.notes?.trim() ?? "",
                ].filter(Boolean).join("\n\n"),
              });
              invalidateCases();
              onClose();
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {submitting
            ? t("agent.flows.scheduleViewing.submitting", { defaultValue: "Scheduling…" })
            : t("agent.flows.scheduleViewing.submit", { defaultValue: "Schedule viewing" })}
        </Btn>
      </ModalFooter>
    </ModalShell>
  );
}
